import { brand, colors, scaleOf } from "../../config/theme";
import type { Tone } from "../../components/ui";
import type { CentinelaMessageTone } from "../../features/centinela/types";
import type { CentinelaCallOutcome } from "./services/mapCentinelaCall";
import type { CaseLadderStep, CaseUrgency } from "./services/mapCentinelaCase";
import type {
  CallEventType,
  CallOutcome,
  CallPurpose,
  CallSeverity,
  ChipTone,
  PeriodDetailTag,
  ProtocolChannel,
  ProtocolEventStatus,
  ProtocolStepStatus,
  RouteStopKind,
  TripCustodyState,
  WatchStatus,
} from "./types";

export const WATCH_STATUS_TONES = {
  safe: "success",
  warning: "warning",
  alert: "error",
  risk: "error",
  offline: "neutral",
  sin_custodia: "info",
} as const satisfies Record<WatchStatus, Tone>;

export const WATCH_STATUS_ICONS = {
  safe: "ri-shield-check-line",
  warning: "ri-alert-line",
  alert: "ri-alarm-warning-line",
  risk: "ri-shield-cross-line",
  offline: "ri-signal-wifi-off-line",
  sin_custodia: "ri-eye-off-line",
} as const satisfies Record<WatchStatus, string>;

export const CALL_OUTCOME_TONES = {
  confirmed: "success",
  false_alarm: "info",
  escalated: "error",
  no_answer: "neutral",
} as const satisfies Record<CallOutcome, Tone>;

export const AI_MESSAGE_TONES = {
  info: "neutral",
  warn: "warning",
  alert: "error",
} as const satisfies Record<CentinelaMessageTone, ChipTone>;

export const AI_MESSAGE_ICONS = {
  info: "ri-information-line",
  warn: "ri-alert-line",
  alert: "ri-alarm-warning-line",
} as const satisfies Record<CentinelaMessageTone, string>;

export const NARRATIVE_CARD_CLASSES = {
  info: "border-primary-500/30 from-primary-500/10",
  warn: "border-warning/40 from-warning/10",
  alert: "border-error/50 from-error/10",
} as const satisfies Record<CentinelaMessageTone, string>;

export const CENTINELA_CALL_TYPE_ICONS: Record<string, string> = {
  rutina: "ri-phone-line",
  sin_gps: "ri-signal-wifi-off-line",
  ajuste_de_ruta: "ri-route-line",
  jamming: "ri-broadcast-line",
  desvio_ruta: "ri-direction-line",
  parada_no_autorizada: "ri-map-pin-line",
  parada_sospechosa: "ri-emotion-unhappy-line",
  exceso_velocidad: "ri-speed-up-line",
  zona_peligro: "ri-alarm-warning-line",
  descanso: "ri-hotel-bed-line",
  comportamiento: "ri-steering-line",
  emergencia: "ri-alarm-warning-fill",
  confirmacion_entrega: "ri-checkbox-circle-line",
  recado: "ri-chat-quote-line",
  seguimiento: "ri-history-line",
};

export const CENTINELA_CALL_OUTCOME_TONES = {
  confirmed: "success",
  answered: "info",
  noAnswer: "neutral",
  escalated: "error",
  duress: "error",
} as const satisfies Record<CentinelaCallOutcome, Tone>;

export const CALL_TYPE_ICONS = {
  route_deviation: "ri-route-line",
  unauthorized_stop: "ri-map-pin-line",
  danger_zone: "ri-alarm-warning-line",
  speeding: "ri-speed-up-line",
  routine: "ri-phone-line",
  suspicious_stop: "ri-emotion-unhappy-line",
  gps_loss: "ri-signal-wifi-off-line",
  jamming: "ri-broadcast-line",
  delivery_confirmation: "ri-checkbox-circle-line",
} as const satisfies Record<string, string>;

export const CALL_TYPE_SEVERITY = {
  route_deviation: "medium",
  unauthorized_stop: "high",
  danger_zone: "medium",
  speeding: "medium",
  routine: "low",
  suspicious_stop: "medium",
  gps_loss: "high",
  jamming: "high",
  delivery_confirmation: "info",
} as const satisfies Record<CallEventType, CallSeverity>;

export const CALL_SEVERITY_TONES = {
  high: "error",
  medium: "warning",
  low: "success",
  info: "info",
} as const satisfies Record<CallSeverity, Tone>;

export const CHART_COLORS = {
  primary: "rgb(var(--c-primary))",
  success: "rgb(var(--c-success))",
  warning: "rgb(var(--c-warning))",
  error: "rgb(var(--c-error))",
  info: "rgb(var(--c-info))",
  muted: "rgb(var(--c-content-muted))",
  grid: "rgb(var(--c-border))",
} as const satisfies Record<string, string>;

export const CASE_STATUS_TONES: Record<string, Tone> = {
  abierto: "warning",
  escalado: "error",
  resuelto: "success",
};

export const CASE_URGENCY_TONES = {
  baja: "neutral",
  media: "warning",
  alta: "error",
  critica: "error",
} as const satisfies Record<CaseUrgency, Tone>;

export const CASE_STEP_STATUS_TONES: Record<string, Tone> = {
  ejecutado: "success",
  fallido: "error",
  pendiente: "neutral",
  cancelado: "neutral",
};

export const CASE_STEP_CHANNEL_ICONS: Record<string, string> = {
  llamada: "ri-phone-line",
  email: "ri-mail-line",
  sms: "ri-message-2-line",
  notificacion_push: "ri-notification-3-line",
  pantalla: "ri-computer-line",
  accion_interna: "ri-settings-3-line",
};

export const CASE_LADDER_ICONS = {
  detectado: "ri-radar-line",
  llamando: "ri-phone-line",
  escalado: "ri-user-star-line",
  resuelto: "ri-check-double-line",
} as const satisfies Record<CaseLadderStep, string>;

export const WATCH_STATUS_WEIGHT = {
  risk: 0,
  alert: 1,
  offline: 2,
  warning: 3,
  sin_custodia: 4,
  safe: 5,
} as const satisfies Record<WatchStatus, number>;

export const WATCH_STATUS_MAP_COLORS = {
  safe: scaleOf(colors.light, "success")["500"],
  warning: scaleOf(colors.light, "warning")["500"],
  alert: scaleOf(colors.light, "error")["500"],
  risk: scaleOf(colors.light, "error")["600"],
  offline: colors.light.text.muted,
  sin_custodia: scaleOf(colors.light, "info")["500"],
} as const satisfies Record<WatchStatus, string>;

export const GEOFENCE_TYPE_MAP_COLORS: Record<string, string> = {
  warehouse: scaleOf(colors.light, "info")["500"],
  customer: scaleOf(colors.light, "success")["500"],
  authorizedStop: brand.primary,
  restrictedArea: scaleOf(colors.light, "error")["500"],
  risk: scaleOf(colors.light, "error")["500"],
  route: scaleOf(colors.light, "warning")["500"],
  truck_yard: scaleOf(colors.light, "secondary")["500"],
  yard: scaleOf(colors.light, "secondary")["500"],
  rest: scaleOf(colors.light, "info")["400"],
  default: colors.light.text.muted,
};

export const WATCH_STATUS_CHART_COLORS = {
  safe: CHART_COLORS.success,
  warning: CHART_COLORS.warning,
  alert: CHART_COLORS.error,
  risk: CHART_COLORS.error,
  offline: CHART_COLORS.muted,
  sin_custodia: CHART_COLORS.info,
} as const satisfies Record<WatchStatus, string>;

export const CHIP_TONE_CLASSES = {
  success: "bg-success/10 text-success border-success/30",
  info: "bg-info/10 text-info border-info/30",
  warning: "bg-warning/10 text-warning border-warning/30",
  error: "bg-error/10 text-error border-error/30",
  neutral: "bg-subtle text-content-muted border-line",
} as const satisfies Record<ChipTone, string>;

export interface WatchStatusCardStyle {
  border: string;
  header: string;
  icon: string;
  dot: string;
  message: string;
}

export const WATCH_STATUS_CARD = {
  safe: {
    border: "border-line",
    header: "bg-card",
    icon: "bg-success/10 text-success",
    dot: "bg-success",
    message: "text-content-secondary",
  },
  warning: {
    border: "border-warning/30",
    header: "bg-warning/5",
    icon: "bg-warning/10 text-warning",
    dot: "bg-warning",
    message: "text-warning",
  },
  alert: {
    border: "border-error/30",
    header: "bg-error/5",
    icon: "bg-error/10 text-error",
    dot: "bg-error",
    message: "text-error",
  },
  risk: {
    border: "border-error/50",
    header: "bg-error/10",
    icon: "bg-error/10 text-error",
    dot: "bg-error animate-pulse",
    message: "text-error",
  },
  offline: {
    border: "border-line",
    header: "bg-subtle/60",
    icon: "bg-subtle text-content-muted",
    dot: "bg-content-muted",
    message: "text-content-muted",
  },
  sin_custodia: {
    border: "border-info/30",
    header: "bg-info/5",
    icon: "bg-info/10 text-info",
    dot: "bg-info",
    message: "text-info",
  },
} as const satisfies Record<WatchStatus, WatchStatusCardStyle>;

export interface GeofenceTypeMeta {
  iconClass: string;
  icon: string;
  header: string;
  chip: string;
}

export const GEOFENCE_TYPE_META: Record<string, GeofenceTypeMeta> = {
  warehouse: {
    iconClass: "ri-building-2-line",
    icon: "bg-info/10 text-info",
    header: "bg-info/5",
    chip: "border-info/30 bg-info/10 text-info",
  },
  customer: {
    iconClass: "ri-store-2-line",
    icon: "bg-success/10 text-success",
    header: "bg-success/5",
    chip: "border-success/30 bg-success/10 text-success",
  },
  authorizedStop: {
    iconClass: "ri-parking-box-line",
    icon: "bg-primary-500/10 text-primary-500",
    header: "bg-primary-500/5",
    chip: "border-primary-500/30 bg-primary-500/10 text-primary-500",
  },
  restrictedArea: {
    iconClass: "ri-alarm-warning-line",
    icon: "bg-error/10 text-error",
    header: "bg-error/5",
    chip: "border-error/30 bg-error/10 text-error",
  },
  route: {
    iconClass: "ri-route-line",
    icon: "bg-warning/10 text-warning",
    header: "bg-warning/5",
    chip: "border-warning/30 bg-warning/10 text-warning",
  },
  truck_yard: {
    iconClass: "ri-truck-line",
    icon: "bg-subtle text-content-secondary",
    header: "bg-subtle/60",
    chip: "border-line bg-subtle text-content-secondary",
  },
  default: {
    iconClass: "ri-map-pin-5-line",
    icon: "bg-subtle text-content-secondary",
    header: "bg-card",
    chip: "border-line bg-subtle text-content-secondary",
  },
};

export const CALL_PURPOSE_CHART_COLORS = {
  routineAndCustody: CHART_COLORS.primary,
  alerts: CHART_COLORS.error,
  deliveries: CHART_COLORS.info,
} as const satisfies Record<CallPurpose, string>;

export const PROTOCOL_CHANNEL_ICONS = {
  screen: "ri-computer-line",
  sms: "ri-message-2-line",
  email: "ri-mail-line",
  call: "ri-phone-line",
  whatsapp: "ri-whatsapp-line",
  push: "ri-notification-3-line",
  internal: "ri-settings-3-line",
} as const satisfies Record<ProtocolChannel, string>;

export const PROTOCOL_STEP_TONES = {
  done: "success",
  failed: "error",
  skipped: "neutral",
  pending: "warning",
} as const satisfies Record<ProtocolStepStatus, Tone>;

export const PROTOCOL_STEP_DOTS = {
  done: "bg-success",
  failed: "bg-error",
  skipped: "bg-content-muted",
  pending: "bg-warning",
} as const satisfies Record<ProtocolStepStatus, string>;

export const PROTOCOL_STEP_CARDS = {
  done: "border-line bg-card",
  failed: "border-error/30 bg-error/5",
  skipped: "border-line-soft bg-subtle/40 opacity-70",
  pending: "border-warning/30 bg-warning/5",
} as const satisfies Record<ProtocolStepStatus, string>;

export const PROTOCOL_EVENT_TONES = {
  completed: "success",
  inProgress: "warning",
  partialFailure: "error",
  cancelled: "neutral",
} as const satisfies Record<ProtocolEventStatus, Tone>;

export const PERIOD_DETAIL_TAG_TONES = {
  included: "success",
  limited: "warning",
  cost: "neutral",
} as const satisfies Record<PeriodDetailTag, Tone>;

interface CustodyProfileMeta {
  icon: string;
  chip: string;
}

const CUSTODY_PROFILE_FALLBACK_META = {
  icon: "ri-shield-line",
  chip: "bg-info/10 text-info",
} as const satisfies CustodyProfileMeta;

export const CUSTODY_PROFILE_META = {
  normal: CUSTODY_PROFILE_FALLBACK_META,
  intensivo: {
    icon: "ri-shield-flash-line",
    chip: "bg-warning/10 text-warning",
  },
  critico: {
    icon: "ri-shield-check-line",
    chip: "bg-error/10 text-error",
  },
  personalizado: {
    icon: "ri-equalizer-line",
    chip: "bg-content-secondary/10 text-content-secondary",
  },
} as const satisfies Record<string, CustodyProfileMeta>;

export const custodyProfileMeta = (profileId: string): CustodyProfileMeta =>
  CUSTODY_PROFILE_META[profileId as keyof typeof CUSTODY_PROFILE_META] ??
  CUSTODY_PROFILE_FALLBACK_META;

export const TRIP_CUSTODY_META = {
  active: {
    icon: "ri-shield-check-line",
    chip: "border-success/30 bg-success/10 text-success",
  },
  scheduled: {
    icon: "ri-shield-flash-line",
    chip: "border-warning/30 bg-warning/10 text-warning",
  },
  none: {
    icon: "ri-shield-line",
    chip: "border-line bg-subtle text-content-muted",
  },
} as const satisfies Record<TripCustodyState, { icon: string; chip: string }>;

export const ROUTE_STOP_DOTS = {
  origin: "bg-primary-500",
  stop: "bg-warning",
  destination: "bg-info",
} as const satisfies Record<RouteStopKind, string>;
